import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Trash2, Upload } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { CrmTabs } from "@/components/admin/CrmTabs";
import { api } from "@/lib/api";
import { inputCls, Field, Btn, Th, Td, TableWrap, EmptyRow } from "@/components/admin/ui";

const DOC_TYPES = ["agreement", "w9", "insurance", "authority", "factoring", "other"];

export default function CrmDocuments() {
  const [docs, setDocs] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [docType, setDocType] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [form, setForm] = useState({ carrier_id: "", doc_type: "agreement", expires_at: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { carrier_id } = useParams();

  const load = useCallback(() => {
    const params = {};
    if (carrier_id) params.carrier_id = carrier_id;
    if (docType) params.doc_type = docType;
    if (expiringOnly) params.expiring_days = 30;
    api.get("/crm/documents", { params }).then(({ data }) => setDocs(data)).catch(() => {});
  }, [carrier_id, docType, expiringOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/crm/carriers").then(({ data }) => {
      setCarriers(data);
      if (carrier_id) setForm((f) => ({ ...f, carrier_id }));
    }).catch(() => {});
  }, [carrier_id]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file first");
    setUploading(true);
    const fd = new FormData();
    fd.append("carrier_id", form.carrier_id);
    fd.append("doc_type", form.doc_type);
    if (form.expires_at) fd.append("expires_at", form.expires_at);
    fd.append("file", file);
    try {
      await api.post("/crm/documents", fd);
      toast.success("Document uploaded");
      setForm({ carrier_id: carrier_id || "", doc_type: "agreement", expires_at: "" });
      setFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/crm/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/crm/documents/${d.id}`);
      toast.success("Document removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const expiryBadge = (d) => {
    if (!d.expires_at) return <span className="text-neutral-600">—</span>;
    if (d.expired) return <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 border border-red-400/50 text-red-400" data-testid={`doc-expired-${d.id}`}>Expired {d.expires_at}</span>;
    if (d.expiring_soon) return <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 border border-yellow-400/50 text-yellow-400">Expires {d.expires_at}</span>;
    return <span className="font-mono text-[10px] text-neutral-400">{d.expires_at}</span>;
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="crm-documents-page">
      <AdminNav />
      <CrmTabs />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl mb-6">Documents</h1>

        <form onSubmit={upload} className="border border-white/15 bg-[#101010] p-5 sm:p-7 mb-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end" data-testid="document-upload-form">
          <Field label="Carrier *" id="d-carrier">
            <select id="d-carrier" data-testid="doc-carrier-select" required value={form.carrier_id} onChange={(e) => setForm({ ...form, carrier_id: e.target.value })} className={`${inputCls} bg-[#101010]`}>
              <option value="">Select carrier...</option>
              {carriers.map((c) => <option key={c.id} value={c.id}>{c.carrier_code} — {c.company}</option>)}
            </select>
          </Field>
          <Field label="Type" id="d-type">
            <select id="d-type" data-testid="doc-type-select" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className={`${inputCls} bg-[#101010]`}>
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
            </select>
          </Field>
          <Field label="Expires" id="d-exp"><input id="d-exp" data-testid="doc-expiry-input" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={inputCls} /></Field>
          <Field label="File *" id="d-file"><input id="d-file" data-testid="doc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm text-neutral-400 file:mr-3 file:bg-white file:text-black file:border-0 file:px-3 file:py-2 file:font-mono file:text-[10px] file:uppercase file:cursor-pointer" /></Field>
          <Btn type="submit" data-testid="doc-upload-button" disabled={uploading}><Upload size={13} /> {uploading ? "Uploading..." : "Upload"}</Btn>
        </form>

        <div className="flex flex-wrap gap-2 mb-4">
          <select data-testid="docs-type-filter" value={docType} onChange={(e) => setDocType(e.target.value)} className={`${inputCls} w-auto bg-[#0B0B0B]`}>
            <option value="">All types</option>
            {DOC_TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400 cursor-pointer">
            <input type="checkbox" data-testid="docs-expiring-filter" checked={expiringOnly} onChange={(e) => setExpiringOnly(e.target.checked)} className="accent-white" />
            Expiring in 30 days
          </label>
        </div>

        <TableWrap testid="documents-table">
          <thead>
            <tr className="border-b border-white/10"><Th>Carrier</Th><Th>Type</Th><Th>File</Th><Th>Size</Th><Th>Expiry</Th><Th>Uploaded</Th><Th></Th></tr>
          </thead>
          <tbody>
            {docs.length === 0 ? (
              <EmptyRow cols={7}>No documents uploaded</EmptyRow>
            ) : (
              docs.map((d) => (
                <tr key={d.id} className="border-b border-white/5" data-testid={`doc-row-${d.id}`}>
                  <Td className="text-neutral-300">{d.carrier_name}</Td>
                  <Td><span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 bg-white/10">{d.doc_type}</span></Td>
                  <Td className="text-neutral-300 max-w-[220px] truncate">{d.original_filename}</Td>
                  <Td className="font-mono text-xs text-neutral-500">{Math.round(d.size / 1024)} KB</Td>
                  <Td>{expiryBadge(d)}</Td>
                  <Td className="font-mono text-xs text-neutral-500">{new Date(d.created_at).toLocaleDateString()}</Td>
                  <Td>
                    <div className="flex gap-3">
                      <button data-testid={`doc-download-${d.id}`} onClick={() => download(d)} className="text-neutral-400 hover:text-white" aria-label="Download"><Download size={15} /></button>
                      <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d)} className="text-neutral-600 hover:text-red-400" aria-label="Delete"><Trash2 size={15} /></button>
                    </div>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </TableWrap>
      </main>
    </div>
  );
}
